import { useState, useMemo, useEffect, useCallback } from 'react';
import useAppStore from '../../store/app.store';
import { IcUsers, IcHandshake, IcBuilding, IcClock, IcMapPin, IcLink, IcEye, IcCalendar, IcUser, IcList } from '../../components/ui/Icons';
import PageHeader from '../../components/ui/PageHeader';
import Modal from '../../components/ui/Modal';
import Badge from '../../components/ui/Badge';
import toast from 'react-hot-toast';
import { format, parseISO, isToday as dateFnsIsToday } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import api from '../../services/api';

const TIPO_COR = {
  cliente: 'bg-blue-100 text-blue-700 border-blue-200',
  interna: 'bg-purple-100 text-purple-700 border-purple-200',
};
const STATUS_COR = { agendada: 'azul', realizada: 'verde', cancelada: 'cinza' };

function avatarInicial(nome = '') { return nome.charAt(0).toUpperCase(); }

function CardReuniao({ reuniao, usuariosMap, onEdit, onDelete, onVerDetalhes }) {
  const usuario = useAppStore(s => s.usuario);
  const isAdmin = usuario?.nivel_acesso <= 1;
  const podEditar = isAdmin || reuniao.criado_por === usuario?.id;
  const dataObj = reuniao.data ? parseISO(reuniao.data) : null;
  const hoje = dataObj ? dateFnsIsToday(dataObj) : false;
  const clienteNome = reuniao.cliente_fantasia || reuniao.cliente_nome || '';

  return (
    <div className={`bg-white rounded-xl border p-4 shadow-sm hover:shadow-md transition-shadow ${hoje ? 'border-brand-300 ring-1 ring-brand-200' : 'border-gray-100'}`}>
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border inline-flex items-center gap-1 ${TIPO_COR[reuniao.tipo]}`}>
              {reuniao.tipo === 'cliente' ? <><IcHandshake size={10} /> Cliente</> : <><IcUsers size={10} /> Interna</>}
            </span>
            <Badge variant={STATUS_COR[reuniao.status]}>{reuniao.status}</Badge>
            {hoje && <span className="text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-bold border border-amber-200">HOJE</span>}
          </div>
          <h3 className="font-semibold text-gray-900 text-sm leading-snug truncate">{reuniao.titulo}</h3>
          {reuniao.tipo === 'cliente' && clienteNome && (
            <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1"><IcBuilding size={11} /> {clienteNome}</p>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-3 text-xs text-gray-500 mb-3">
        <span className="flex items-center gap-1">
          <IcCalendar size={11} /> {dataObj ? format(dataObj, "dd 'de' MMMM", { locale: ptBR }) : '—'}
        </span>
        <span className="flex items-center gap-1">
          <IcClock size={11} /> {reuniao.hora?.slice(0,5)}{reuniao.duracao ? ` · ${reuniao.duracao} min` : ''}
        </span>
        {reuniao.local && <span className="flex items-center gap-1"><IcMapPin size={11} /> {reuniao.local}</span>}
      </div>

      {reuniao.link_reuniao && (
        <a href={reuniao.link_reuniao} target="_blank" rel="noopener noreferrer"
          className="flex items-center gap-1.5 text-xs text-brand-600 hover:text-brand-700 font-medium mb-3 truncate">
          <IcLink size={12} /> Entrar na reunião
        </a>
      )}

      <div className="flex items-center justify-between">
        <div className="flex -space-x-2">
          {(reuniao.participantes || []).slice(0, 5).map(pid => (
            <div key={pid} title={usuariosMap[pid] || pid}
              className="w-6 h-6 rounded-full bg-brand-100 text-brand-700 text-[10px] font-bold flex items-center justify-center border-2 border-white">
              {avatarInicial(usuariosMap[pid] || '?')}
            </div>
          ))}
          {(reuniao.participantes || []).length > 5 && (
            <div className="w-6 h-6 rounded-full bg-gray-100 text-gray-500 text-[10px] font-bold flex items-center justify-center border-2 border-white">
              +{reuniao.participantes.length - 5}
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <button onClick={() => onVerDetalhes(reuniao)} className="text-xs text-gray-400 hover:text-gray-700 flex items-center gap-1"><IcEye size={12} /> Ver</button>
          {podEditar && (
            <>
              <button onClick={() => onEdit(reuniao)} className="text-xs text-brand-600 hover:text-brand-700 font-medium">Editar</button>
              <button onClick={() => onDelete(reuniao.id)} className="text-xs text-red-400 hover:text-red-600">Excluir</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ModalDetalhes({ reuniao, usuariosMap, onClose, onEdit }) {
  const usuario = useAppStore(s => s.usuario);
  const podEditar = usuario?.nivel_acesso <= 1 || reuniao.criado_por === usuario?.id;
  const dataObj = reuniao.data ? parseISO(reuniao.data) : null;
  const clienteNome = reuniao.cliente_fantasia || reuniao.cliente_nome || '';

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-xs font-semibold px-2 py-1 rounded-full border ${TIPO_COR[reuniao.tipo]}`}>
          {reuniao.tipo === 'cliente' ? 'Reunião com Cliente' : 'Reunião Interna'}
        </span>
        <Badge variant={STATUS_COR[reuniao.status]}>{reuniao.status}</Badge>
      </div>

      <div>
        <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Título</p>
        <p className="font-semibold text-gray-900">{reuniao.titulo}</p>
      </div>

      {reuniao.tipo === 'cliente' && clienteNome && (
        <div>
          <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Cliente</p>
          <p className="text-sm text-gray-700 flex items-center gap-1"><IcBuilding size={13} /> {clienteNome}</p>
        </div>
      )}

      {reuniao.descricao && (
        <div>
          <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Descrição</p>
          <p className="text-sm text-gray-600 leading-relaxed">{reuniao.descricao}</p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div>
          <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Data</p>
          <p className="text-sm text-gray-700">{dataObj ? format(dataObj, "dd 'de' MMMM 'de' yyyy", { locale: ptBR }) : '—'}</p>
        </div>
        <div>
          <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Horário</p>
          <p className="text-sm text-gray-700">{reuniao.hora?.slice(0,5)}{reuniao.duracao ? ` (${reuniao.duracao} min)` : ''}</p>
        </div>
      </div>

      {reuniao.local && (
        <div>
          <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Local</p>
          <p className="text-sm text-gray-700">{reuniao.local}</p>
        </div>
      )}

      {reuniao.link_reuniao && (
        <div>
          <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Link</p>
          <a href={reuniao.link_reuniao} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-2 text-sm text-brand-600 hover:text-brand-700 font-medium bg-brand-50 px-3 py-2 rounded-lg border border-brand-100">
            <IcLink size={14} /> {reuniao.link_reuniao}
          </a>
        </div>
      )}

      <div>
        <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">Participantes ({(reuniao.participantes || []).length})</p>
        <div className="flex flex-wrap gap-2">
          {(reuniao.participantes || []).map(pid => (
            <div key={pid} className="flex items-center gap-1.5 bg-gray-50 border border-gray-100 rounded-full px-2 py-1">
              <div className="w-5 h-5 rounded-full bg-brand-100 text-brand-700 text-[10px] font-bold flex items-center justify-center">
                {avatarInicial(usuariosMap[pid] || '?')}
              </div>
              <span className="text-xs text-gray-700">{usuariosMap[pid] || pid}</span>
            </div>
          ))}
        </div>
      </div>

      {podEditar && (
        <div className="flex gap-2 pt-2 border-t border-gray-100">
          <button onClick={() => { onEdit(reuniao); onClose(); }} className="btn-primary flex-1">Editar reunião</button>
          <button onClick={onClose} className="btn-secondary">Fechar</button>
        </div>
      )}
      {!podEditar && <button onClick={onClose} className="btn-secondary w-full">Fechar</button>}
    </div>
  );
}

function FormReuniao({ inicial, usuarios, onSalvar, onFechar, salvando }) {
  const usuario = useAppStore(s => s.usuario);
  const hoje = new Date().toISOString().slice(0, 10);
  const [clientes, setClientes] = useState([]);

  const [form, setForm] = useState({
    titulo: '', descricao: '', tipo: 'cliente', cliente_id: '',
    data: hoje, hora: '09:00', duracao: 60,
    participantes: [usuario?.id].filter(Boolean),
    link_reuniao: '', local: '', status: 'agendada',
    ...inicial,
    hora: inicial?.hora?.slice(0,5) || '09:00',
  });

  useEffect(() => {
    api.get('/clientes/lista').then(r => setClientes(r.data || [])).catch(() => {});
  }, []);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const toggleParticipante = (uid) => {
    setForm(p => ({
      ...p,
      participantes: p.participantes.includes(uid)
        ? p.participantes.filter(id => id !== uid)
        : [...p.participantes, uid],
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.titulo.trim()) { toast.error('Informe o título da reunião.'); return; }
    if (!form.data) { toast.error('Informe a data.'); return; }
    if (!form.hora) { toast.error('Informe o horário.'); return; }
    if (form.participantes.length === 0) { toast.error('Adicione pelo menos um participante.'); return; }
    onSalvar({ ...form, duracao: parseInt(form.duracao) || 60 });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="form-label">Tipo de reunião</label>
        <div className="grid grid-cols-2 gap-2">
          {[
            { val: 'cliente', label: 'Com Cliente', desc: 'Reunião com cliente externo' },
            { val: 'interna', label: 'Interna', desc: 'Reunião da equipe' },
          ].map(t => (
            <button key={t.val} type="button" onClick={() => set('tipo', t.val)}
              className={`p-3 rounded-xl border-2 text-left transition-all ${form.tipo === t.val ? 'border-brand-400 bg-brand-50' : 'border-gray-200 bg-white hover:border-brand-200'}`}>
              <p className="text-sm font-semibold text-gray-800">{t.label}</p>
              <p className="text-[10px] text-gray-400 mt-0.5">{t.desc}</p>
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="form-label">Título *</label>
        <input className="form-input" value={form.titulo} onChange={e => set('titulo', e.target.value)} required />
      </div>

      {form.tipo === 'cliente' && (
        <div>
          <label className="form-label">Cliente</label>
          <select className="form-input" value={form.cliente_id} onChange={e => set('cliente_id', e.target.value)}>
            <option value="">Selecione o cliente...</option>
            {clientes.map(c => <option key={c.id} value={c.id}>{c.nome_fantasia || c.razao_social}</option>)}
          </select>
        </div>
      )}

      <div>
        <label className="form-label">Descrição / Pauta</label>
        <textarea className="form-input" rows={3} value={form.descricao} onChange={e => set('descricao', e.target.value)} />
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="form-label">Data *</label>
          <input type="date" className="form-input" value={form.data} onChange={e => set('data', e.target.value)} required />
        </div>
        <div>
          <label className="form-label">Horário *</label>
          <input type="time" className="form-input" value={form.hora} onChange={e => set('hora', e.target.value)} required />
        </div>
        <div>
          <label className="form-label">Duração (min)</label>
          <input type="number" className="form-input" value={form.duracao} onChange={e => set('duracao', e.target.value)} min={15} step={15} />
        </div>
      </div>

      <div>
        <label className="form-label">Local (presencial)</label>
        <input className="form-input" value={form.local} onChange={e => set('local', e.target.value)} />
      </div>

      <div>
        <label className="form-label">Link da reunião (online)</label>
        <input className="form-input" value={form.link_reuniao} onChange={e => set('link_reuniao', e.target.value)} placeholder="https://meet.google.com/..." />
      </div>

      <div>
        <label className="form-label mb-2">Participantes</label>
        <div className="space-y-1.5 max-h-48 overflow-y-auto border border-gray-100 rounded-xl p-2">
          {usuarios.map(u => {
            const marcado = form.participantes.includes(u.id);
            const ehVoce = u.id === usuario?.id;
            return (
              <label key={u.id}
                className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${marcado ? 'bg-brand-50 border border-brand-100' : 'hover:bg-gray-50'}`}>
                <input type="checkbox" checked={marcado} disabled={ehVoce}
                  onChange={() => !ehVoce && toggleParticipante(u.id)} className="w-4 h-4 accent-brand-600" />
                <div className="w-6 h-6 rounded-full bg-brand-100 text-brand-700 text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                  {avatarInicial(u.nome)}
                </div>
                <span className="text-sm text-gray-800">{u.nome} {ehVoce && <span className="text-[10px] text-gray-400">(você)</span>}</span>
              </label>
            );
          })}
        </div>
      </div>

      {inicial?.id && (
        <div>
          <label className="form-label">Status</label>
          <select className="form-input" value={form.status} onChange={e => set('status', e.target.value)}>
            <option value="agendada">Agendada</option>
            <option value="realizada">Realizada</option>
            <option value="cancelada">Cancelada</option>
          </select>
        </div>
      )}

      <div className="flex gap-2 pt-2 border-t border-gray-100">
        <button type="submit" disabled={salvando} className="btn-primary flex-1">
          {salvando ? 'Salvando...' : (inicial?.id ? 'Salvar alterações' : 'Agendar reunião')}
        </button>
        <button type="button" onClick={onFechar} className="btn-secondary">Cancelar</button>
      </div>
    </form>
  );
}

export default function Reunioes() {
  const usuario = useAppStore(s => s.usuario);
  const [reunioes, setReunioes] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [aba, setAba] = useState('proximas');
  const [modalNova, setModalNova] = useState(false);
  const [editando, setEditando] = useState(null);
  const [detalhes, setDetalhes] = useState(null);

  const usuariosMap = useMemo(() => Object.fromEntries(usuarios.map(u => [u.id, u.nome])), [usuarios]);

  const carregarReunioes = useCallback(async (abaAtual = aba) => {
    try {
      setLoading(true);
      const { data } = await api.get('/reunioes', { params: { aba: abaAtual } });
      setReunioes(data || []);
    } catch {
      toast.error('Erro ao carregar reuniões.');
    } finally {
      setLoading(false);
    }
  }, [aba]);

  useEffect(() => {
    carregarReunioes(aba);
  }, [aba]);

  useEffect(() => {
    api.get('/usuarios/equipe').then(r => setUsuarios(r.data || [])).catch(() => {});
  }, []);

  const hoje = new Date().toISOString().slice(0, 10);
  const proximas = reunioes.filter(r => r.status === 'agendada' && r.data >= hoje);
  const hojeCount = reunioes.filter(r => r.data === hoje && r.status === 'agendada');
  const minhasProximas = proximas.filter(r => (r.participantes || []).includes(usuario?.id));

  const handleSalvar = async (dados) => {
    setSalvando(true);
    try {
      if (editando?.id) {
        await api.patch(`/reunioes/${editando.id}`, dados);
        toast.success('Reunião atualizada!');
      } else {
        await api.post('/reunioes', dados);
        toast.success('Reunião agendada!');
      }
      setModalNova(false);
      setEditando(null);
      carregarReunioes(aba);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao salvar reunião.');
    } finally {
      setSalvando(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remover esta reunião?')) return;
    try {
      await api.delete(`/reunioes/${id}`);
      toast.success('Reunião removida.');
      carregarReunioes(aba);
    } catch {
      toast.error('Erro ao remover reunião.');
    }
  };

  return (
    <div>
      <PageHeader
        title="Reuniões"
        subtitle="Gerencie reuniões com clientes e equipe"
        actions={<button onClick={() => setModalNova(true)} className="btn-primary">+ Nova Reunião</button>}
      />

      <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-6 resp-grid-3">
        <div className="stat-card text-center">
          <p className="text-3xl font-bold text-gray-900">{proximas.length}</p>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mt-1">Agendadas</p>
        </div>
        <div className="stat-card text-center">
          <p className="text-3xl font-bold text-amber-600">{hojeCount.length}</p>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mt-1">Hoje</p>
        </div>
        <div className="stat-card text-center">
          <p className="text-3xl font-bold text-brand-600">{minhasProximas.length}</p>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mt-1">Minhas próximas</p>
        </div>
      </div>

      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 mb-6 w-fit">
        {[
          { key: 'proximas', label: <><IcCalendar size={13} /> Próximas</> },
          { key: 'minhas',   label: <><IcUser size={13} /> Minhas</> },
          { key: 'passadas', label: <><IcList size={13} /> Histórico</> },
        ].map(t => (
          <button key={t.key} onClick={() => setAba(t.key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${aba === t.key ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-sm text-gray-400 text-center py-12">Carregando...</p>
      ) : reunioes.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {reunioes.map(r => (
            <CardReuniao key={r.id} reuniao={r} usuariosMap={usuariosMap}
              onEdit={setEditando} onDelete={handleDelete} onVerDetalhes={setDetalhes} />
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
          <div className="flex justify-center mb-3 text-gray-300"><IcCalendar size={44} /></div>
          <p className="text-gray-500 font-medium">Nenhuma reunião encontrada</p>
          <p className="text-sm text-gray-400 mt-1">
            {aba === 'proximas' ? 'Agende uma reunião clicando em + Nova Reunião.' : 'Sem histórico nesta aba.'}
          </p>
        </div>
      )}

      <Modal open={modalNova || !!editando} onClose={() => { setModalNova(false); setEditando(null); }}
        title={editando ? 'Editar Reunião' : 'Nova Reunião'} size="lg">
        <FormReuniao inicial={editando || undefined} usuarios={usuarios}
          onSalvar={handleSalvar} onFechar={() => { setModalNova(false); setEditando(null); }} salvando={salvando} />
      </Modal>

      <Modal open={!!detalhes} onClose={() => setDetalhes(null)} title="Detalhes da Reunião" size="md">
        {detalhes && (
          <ModalDetalhes reuniao={detalhes} usuariosMap={usuariosMap}
            onClose={() => setDetalhes(null)}
            onEdit={(r) => { setDetalhes(null); setEditando(r); }} />
        )}
      </Modal>
    </div>
  );
}
